import React from 'react';
import { TargetIcon, EyeIcon, HeartIcon, ZapIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
export function AboutPage() {
  usePageSEO({
    title: 'About | Design Flow Digitals',
    description: 'Learn about Design Flow Digitals — a Shopify-focused development company building premium ecommerce experiences with custom themes, apps, and conversion-optimized stores.'
  });
  const philosophies = [{
    title: 'Simple',
    icon: <HeartIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-360" />
  }, {
    title: 'Fast',
    icon: <ZapIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-361" />
  }, {
    title: 'Beautiful',
    icon: <EyeIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-362" />
  }, {
    title: 'Effective',
    icon: <TargetIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-363" />
  }];
  return <div className="pt-32 pb-20" data-id="element-364">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-365">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-366">
          About Design Flow Digitals
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-367">
          Design Flow Digitals is a Shopify development company focused on
          building premium ecommerce experiences.
        </p>
      </section>

      {/* Specializations & Goal */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-368">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-369">
          <div data-id="element-370">
            <h2 className="text-3xl font-bold mb-8" data-id="element-371">We specialize in:</h2>
            <ul className="space-y-4" data-id="element-372">
              {['Shopify theme development', 'Shopify app development', 'Custom Shopify store design', 'Performance optimization'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-gray-300" data-id="element-373">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-374" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="glass-card p-10 border-[#D4AF37]/20 relative overflow-hidden" data-id="element-375">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" data-id="element-376" />
            <h2 className="text-2xl font-bold mb-4 text-white" data-id="element-377">
              Our goal is simple:
            </h2>
            <p className="text-2xl text-gradient-gold font-medium leading-relaxed" data-id="element-378">
              Create ecommerce products and experiences that help businesses
              grow faster.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding" data-id="element-379">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-380">
          <h2 className="text-4xl font-bold mb-6" data-id="element-381">Our Philosophy</h2>
          <p className="text-xl text-gray-400" data-id="element-382">
            Great ecommerce design should be:
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-383">
          {philosophies.map((phil, i) => <div key={i} className="glass-card p-8 text-center flex flex-col items-center gap-4" data-id="element-384">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-385">
                {phil.icon}
              </div>
              <h3 className="text-xl font-bold text-white" data-id="element-386">{phil.title}</h3>
            </div>)}
        </div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-387">
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-388">
            Inspired by the product design principles used by companies like
            Apple, we believe that simplicity and performance create the best
            user experiences.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-black" data-id="element-389">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-390">
          <div className="glass-card p-12" data-id="element-391">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-392">
              Our Mission
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-393">
              To help modern ecommerce brands launch and scale with powerful
              Shopify technology.
            </p>
          </div>
          <div className="glass-card p-12" data-id="element-394">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-395">
              Our Vision
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-396">
              To become one of the most trusted creators of Shopify themes,
              apps, and ecommerce experiences globally.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Recognition */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-397">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-398">
          <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-399">
            Industry Recognition
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-400">
            Featured By Leading Platforms
          </h3>
          <p className="text-lg text-gray-400" data-id="element-401">
            Pankaj Maurya recognized as a top Shopify expert by industry leaders
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-402">
          <a href="https://www.omnisend.com/blog/shopify-experts/" target="_blank" rel="noopener noreferrer" className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group" data-id="element-403">
            <div className="flex items-start gap-4" data-id="element-404">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shrink-0" data-id="element-405">
                <span className="text-2xl" data-id="element-406">🏆</span>
              </div>
              <div data-id="element-407">
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-408">
                  Featured by Omnisend
                </h4>
                <p className="text-gray-400 text-sm mb-3" data-id="element-409">
                  Recognized among top Shopify experts for exceptional ecommerce
                  development and design expertise
                </p>
                <span className="text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1" data-id="element-410">
                  Read Article →
                </span>
              </div>
            </div>
          </a>
          <a href="https://ecomposer.io/blogs/ecommerce/best-shopify-web-designers" target="_blank" rel="noopener noreferrer" className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group" data-id="element-411">
            <div className="flex items-start gap-4" data-id="element-412">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shrink-0" data-id="element-413">
                <span className="text-2xl" data-id="element-414">⭐</span>
              </div>
              <div data-id="element-415">
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-416">
                  Featured by EComposer
                </h4>
                <p className="text-gray-400 text-sm mb-3" data-id="element-417">
                  Listed among the best Shopify web designers for creating
                  high-converting, beautifully designed stores
                </p>
                <span className="text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1" data-id="element-418">
                  Read Article →
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Company Details */}
      <section className="section-padding text-center" data-id="element-419">
        <div className="inline-block glass-card p-10 border-white/10" data-id="element-420">
          <h2 className="text-2xl font-bold mb-6" data-id="element-421">Company Details</h2>
          <div className="text-gray-400 space-y-2" data-id="element-422">
            <p className="font-medium text-white" data-id="element-423">Design Flow Digitals</p>
            <p data-id="element-424">Part of Pankaj Maurya LLC</p>
            <p data-id="element-425">67 Wigley Loop</p>
            <p data-id="element-426">Whitwell, TN, 37397</p>
            <p data-id="element-427">United States</p>
            <p className="mt-4 pt-4 border-t border-white/10" data-id="element-428">
              Support:{' '}
              <a href="mailto:support@designflowdigitals.com" className="text-[#D4AF37] hover:underline" data-id="element-429">
                support@designflowdigitals.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>;
}